import React, { useState, useEffect, useRef } from 'react';
import { paginate } from '../../lib/paginate.js';

// Client-side pagination hook for an already-loaded list. Holds the current
// page and derives the slice bounds from the pure `paginate` helper, so the
// rendered slice is always valid — even when `page` state is momentarily stale
// after the list grows or shrinks.
export function usePagination(items, pageSize) {
  const total = items.length;
  const [page, setPage] = useState(1);
  const { totalPages, safePage, start, end } = paginate(total, page, pageSize);

  // Clamp on shrink: if items were removed (e.g. a delete) and the current page
  // fell past the end, snap down to the last valid page so we never render an
  // empty page. Slicing already uses `safePage`, so this only fixes the state.
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  // Reset to page 1 on grow: when a new item is added/prepended (length goes
  // up), jump to the first page so the user actually sees it. Tracked via a ref
  // of the previous length so we never call setState during render.
  const prevTotal = useRef(total);
  useEffect(() => {
    if (total > prevTotal.current) setPage(1);
    prevTotal.current = total;
  }, [total]);

  return {
    pageItems: items.slice(start, end),
    page: safePage,
    setPage,
    totalPages,
    total,
    start,
    end
  };
}

function pagerButtonStyle(disabled) {
  return {
    background: 'none',
    border: 'none',
    padding: 0,
    font: 'inherit',
    color: disabled ? '#9A9A95' : '#3A3A38',
    cursor: disabled ? 'not-allowed' : 'pointer'
  };
}

// Compact, muted pager placed below a list. Renders nothing for a single page.
export default function Pagination({ page, totalPages, total, start, end, setPage }) {
  if (totalPages <= 1) return null;

  const onFirst = page <= 1;
  const onLast = page >= totalPages;

  return (
    <div
      className="flex items-center justify-between mt-3 pt-3 border-t text-[11px]"
      style={{ borderColor: '#F0EFE9', color: '#9A9A95' }}
    >
      <span style={{ fontVariantNumeric: 'tabular-nums' }}>
        {`${start + 1}–${end} of ${total}`}
      </span>
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={() => setPage(page - 1)}
          disabled={onFirst}
          className="text-[12px]"
          style={pagerButtonStyle(onFirst)}
        >‹ Prev</button>
        <button
          type="button"
          onClick={() => setPage(page + 1)}
          disabled={onLast}
          className="text-[12px]"
          style={pagerButtonStyle(onLast)}
        >Next ›</button>
      </div>
    </div>
  );
}
